import { useCallback, useMemo } from 'react';
import { create } from 'zustand';
import bs58 from 'bs58';
import { Client, Room } from 'colyseus.js';
import {
  Wager,
  TurnResolution,
  RunAnomaly,
  DeckCard,
  MATCH_SABOTAGE_CARDS,
  ScoreBreakdown,
  ModifierCard,
  RunShopState,
  JokerDefinition,
  ConsumableDefinition,
} from '@solante/shared';
import { useWallet } from '@solana/wallet-adapter-react';
import { toast } from 'sonner';
import { useClusterStore } from './clusterStore';
import { usePlayerStatsStore, ProgressionUpdate } from './playerStatsStore';
import { shortKey } from '../utils/format';
import { fetchMatchDetails } from '../api/matches';
import { queryClient } from '../main';

type ClientPlayerRunState = {
  chips: number;
  hearts: number;
  totalScore: number;
  jokerSlots: number;
  jokers: JokerDefinition[];
  consumables: ConsumableDefinition[];
  consumablesUsed: string[];
  pendingScorePercent: number;
  tempoStreak: number;
  phase: 'play' | 'shop';
  nextShopTurn: number;
  shopGeneration: number;
  shopRerolls: number;
};

export interface ClientMatchState {
  matchId: string;
  wager: {
    currency: Wager['currency'];
    amountLamports: string;
  };
  players: Array<{
    wallet: string;
    displayName: string;
    avatarSeed: string;
  }>;
  targetScore: number;
  currentTurnWallet?: string;
  timeline: TurnResolution[];
  fairness?: {
    serverSeedHash: string;
    serverSeed?: string;
    clientSeeds: Record<string, string>;
  };
  currentHand?: DeckCard[];
  currentHandOwner?: string;
  currentHandSequence?: number;
  activeAnomaly?: RunAnomaly | null;
  nextAnomaly?: RunAnomaly | null;
  anomalyHistory?: RunAnomaly[];
  playerPowers?: Record<string, string[]>;
  pendingDebuffs?: Record<
    string,
    { scorePenalty?: number; suitLock?: string; shieldCharges?: number }
  >;
  deckStates?: Record<string, { drawPile: string[]; discardPile: string[] }>;
  turnDeadlineIso?: string;
  turnSecondsRemaining?: number;
  tempoState?: Record<string, number>;
  chatMessages: Array<{
    wallet: string;
    displayName: string;
    message: string;
    timestamp: number;
  }>;
  playerRunStates?: Record<string, ClientPlayerRunState>;
  playerShopStates?: Record<string, RunShopState>;
  phase?: 'play' | 'shop';
  escrowedWallets?: string[];
  seedCommitments?: string[];
  status?: string;
  settlement?: {
    matchId: string;
    winner: string;
    currency: Wager['currency'];
    winnerAmount: string;
    treasuryAmount: string;
    totalStake?: string;
    treasuryWallet?: string;
    transactionSignature?: string;
  };
  pendingSettlement?: {
    winner: string;
    reason: SettlementReason;
  };
  opponentDisconnect?: {
    wallet: string;
    remainingTime: number;
  } | null;
  evolutions?: Array<{
    wallet: string;
    fromJoker: { id: string; name: string; tier: string };
    toJoker: { id: string; name: string; tier: string };
  }>;
  rematchRequests?: string[]; // Wallets that have requested rematch
  rematchConfirmed?: boolean;
  rematchWager?: {
    currency: Wager['currency'];
    amountLamports: string;
  };
  // Double-or-nothing state
  doubleOrNothingProposed?: boolean; // Whether winner has proposed double-or-nothing
  doubleOrNothingProposedBy?: string; // Wallet of the proposer (winner)
  doubleOrNothingConfirmed?: boolean;
  doubleOrNothingWager?: {
    currency: Wager['currency'];
    amountLamports: string;
  };
}

interface RoomStore {
  state?: ClientMatchState;
  reconnecting: boolean;
  error?: Error;
  setState: (state?: ClientMatchState) => void;
  setStatus: (status: Partial<Pick<RoomStore, 'reconnecting' | 'error'>>) => void;
}

const useRoomStoreBase = create<RoomStore>((set) => ({
  reconnecting: false,
  setState: (state) => set({ state }),
  setStatus: (status) => set(status),
}));

let activeRoom: Room | undefined;
let activeClient: Client | undefined;
let activeMatchId: string | undefined;
let activeWallet: string | null | undefined;
const standbyRooms = new Map<string, Room>();
const stateHydrators = new Map<string, (rawState: unknown) => void>();
const clientSeeds = new Map<string, string>();
const joinSignatures = new Map<string, string>();
const joinSignaturePromises = new Map<string, Promise<string>>();

// Connection lock to prevent concurrent connection attempts
let connectionInProgress: Promise<void> | null = null;
let connectionLockKey: string | null = null;
let connectionLockTimestamp: number | null = null;
const CONNECTION_LOCK_TIMEOUT_MS = 30_000; // 30 second timeout for stale locks

// Clear stale connection lock if it's been held too long
const clearStaleLock = () => {
  if (
    connectionLockTimestamp &&
    Date.now() - connectionLockTimestamp > CONNECTION_LOCK_TIMEOUT_MS
  ) {
    console.warn('Clearing stale connection lock');
    connectionInProgress = null;
    connectionLockKey = null;
    connectionLockTimestamp = null;
  }
};

// Force clear all connection locks - useful for unmount/navigation
export const clearConnectionLocks = () => {
  connectionInProgress = null;
  connectionLockKey = null;
  connectionLockTimestamp = null;
};
const sabotageNameIndex = new Map(MATCH_SABOTAGE_CARDS.map((card) => [card.id, card.name]));
const TIMEOUT_PENALTY_POINTS = 20;
const TEMPO_MAX_STREAK = 3;
const SIGN_MESSAGE_REQUIRED_ERROR = 'Wallet must support message signing to join wagers';
const CHAT_MESSAGE_LIMIT = 50;
const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });
const MAX_ROOM_JOIN_ATTEMPTS = 5;
type ForEachable<T> = { forEach: (callback: (item: T) => void) => void };

const connectionKeyFor = (matchId: string, wallet: string) => `${matchId}:${wallet}`;

type RawRoomPlayer = {
  wallet?: string;
  displayName?: string;
  avatarSeed?: string;
};

type RawTimelineEntry = {
  sequence?: number;
  player?: string;
  cardsJson?: string;
  modifiersJson?: string;
  breakdownJson?: string;
};

type RawChatMessage = {
  wallet?: string;
  displayName?: string;
  message?: string;
  timestamp?: number;
};

type SettlementReason = 'standard' | 'forfeit' | 'turnLimit';

const safeParseJson = <T>(raw: unknown, fallback: T): T => {
  if (typeof raw !== 'string' || !raw) {
    return fallback;
  }
  try {
    return JSON.parse(raw) as T;
  } catch (error) {
    console.warn('Failed to parse payload', error);
    return fallback;
  }
};

const toLamportString = (value: unknown): string | undefined => {
  if (typeof value === 'string' && value) {
    return value;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.round(value).toString();
  }
  if (typeof value === 'bigint') {
    return value.toString();
  }
  return undefined;
};

const parseSettlementEvent = (raw: unknown): ClientMatchState['settlement'] | undefined => {
  if (!raw || typeof raw !== 'object') {
    return undefined;
  }
  const payload = raw as Record<string, unknown>;
  const matchId = typeof payload.matchId === 'string' ? payload.matchId : undefined;
  const winner = typeof payload.winner === 'string' ? payload.winner : undefined;
  if (!matchId || !winner) {
    return undefined;
  }
  const currency =
    payload.currency === 'USDC' || payload.currency === 'SOL' ? payload.currency : 'SOL';
  const winnerAmount = toLamportString(payload.winnerAmount) ?? '0';
  const treasuryAmount = toLamportString(payload.treasuryAmount) ?? '0';
  const totalStake = toLamportString(payload.totalStake);
  const treasuryWallet =
    typeof payload.treasuryWallet === 'string' && payload.treasuryWallet
      ? payload.treasuryWallet
      : undefined;
  const transactionSignature =
    typeof payload.transactionSignature === 'string' && payload.transactionSignature
      ? payload.transactionSignature
      : undefined;

  return {
    matchId,
    winner,
    currency,
    winnerAmount,
    treasuryAmount,
    totalStake,
    treasuryWallet,
    transactionSignature,
  };
};

export const useRoomConnection = (matchId?: string) => {
  const { cluster } = useClusterStore();
  const { publicKey, signMessage } = useWallet();
  const walletAddress = useMemo(() => publicKey?.toBase58() ?? null, [publicKey]);
  const setState = useRoomStoreBase((s) => s.setState);
  const { reconnecting, error } = useRoomStoreBase((s) => ({
    reconnecting: s.reconnecting,
    error: s.error,
  }));
  const setStatus = useRoomStoreBase((s) => s.setStatus);

  const connect = useCallback(async () => {
    if (!matchId) return;
    const ensuredMatchId = matchId;
    const endpoint = import.meta.env.VITE_WS_ENDPOINT || 'ws://localhost:8787';
    if (!walletAddress) {
      setStatus({ reconnecting: false, error: undefined });
      return;
    }
    const ensuredWalletAddress = walletAddress;
    if (!signMessage) {
      const existing = useRoomStoreBase.getState().error;
      if (existing?.message !== SIGN_MESSAGE_REQUIRED_ERROR) {
        setStatus({
          reconnecting: false,
          error: new Error(SIGN_MESSAGE_REQUIRED_ERROR),
        });
      } else {
        setStatus({ reconnecting: false });
      }
      return;
    }
    const signMessageFn = signMessage;

    const connectionKey = connectionKeyFor(ensuredMatchId, ensuredWalletAddress);

    // If already connected to this room, skip
    if (activeRoom && activeMatchId === ensuredMatchId && activeWallet === ensuredWalletAddress) {
      setStatus({ reconnecting: false, error: undefined });
      return;
    }

    // Clear any stale locks before checking
    clearStaleLock();

    // If a connection is already in progress for this key, wait for it instead of starting a new one
    if (connectionInProgress && connectionLockKey === connectionKey) {
      await connectionInProgress;
      return;
    }

    // If a connection is in progress for a different key, wait for it first
    if (connectionInProgress) {
      await connectionInProgress;
    }

    const cachedRoom = standbyRooms.get(connectionKey);
    if (cachedRoom) {
      activeRoom = cachedRoom;
      standbyRooms.delete(connectionKey);
      activeMatchId = ensuredMatchId;
      activeWallet = ensuredWalletAddress;
      const hydrate = stateHydrators.get(connectionKey);
      if (hydrate) {
        hydrate(cachedRoom.state);
      }
      setStatus({ reconnecting: false, error: undefined });
      return;
    }

    // Acquire connection lock with timestamp for stale detection
    let resolveConnection: () => void = () => {};
    connectionInProgress = new Promise<void>((resolve) => {
      resolveConnection = resolve;
    });
    connectionLockKey = connectionKey;
    connectionLockTimestamp = Date.now();

    const releaseConnectionLock = () => {
      if (connectionLockKey === connectionKey) {
        connectionInProgress = null;
        connectionLockKey = null;
        connectionLockTimestamp = null;
      }
      resolveConnection();
    };

    const match = await fetchMatchDetails(ensuredMatchId).catch((fetchError) => {
      const message =
        fetchError instanceof Error ? fetchError.message : 'Unable to load match details';
      setStatus({ reconnecting: false, error: new Error(message) });
      releaseConnectionLock();
      return;
    });
    if (!match) {
      releaseConnectionLock();
      return;
    }

    if (!match.roomId) {
      // Room not ready - retry with bounded backoff
      const maxRetries = 3;
      const retryDelay = 1200; // ~1.2s between retries
      let attempt = 0;

      const retryConnection = async (): Promise<void> => {
        attempt += 1;
        if (attempt > maxRetries) {
          setStatus({
            reconnecting: false,
            error: new Error('Room setup timed out. Please refresh and try again.'),
          });
          releaseConnectionLock();
          return;
        }

        await new Promise((resolve) => setTimeout(resolve, retryDelay));

        const retryMatch = await fetchMatchDetails(ensuredMatchId).catch(() => null);
        if (!retryMatch || !retryMatch.roomId) {
          return retryConnection();
        }

        // Room is ready, proceed with connection
        await proceedWithConnection(retryMatch);
      };

      setStatus({ reconnecting: true, error: undefined });
      await retryConnection();
      return;
    }

    await proceedWithConnection(match);

    async function proceedWithConnection(match: Awaited<ReturnType<typeof fetchMatchDetails>>) {
      if (!match.roomId) {
        setStatus({
          reconnecting: false,
          error: new Error('Room ID missing after retry. Please try again.'),
        });
        releaseConnectionLock();
        return;
      }

      if (typeof window !== 'undefined') {
        sessionStorage.setItem(`solante:room:${ensuredMatchId}`, match.roomId);
      }

      const signatureCacheKey = `solante:join:${ensuredMatchId}:${ensuredWalletAddress}`;
      let authSignature = joinSignatures.get(signatureCacheKey);
      if (!authSignature && typeof window !== 'undefined') {
        authSignature = sessionStorage.getItem(signatureCacheKey) ?? undefined;
      }
      if (!authSignature) {
        let signaturePromise = joinSignaturePromises.get(signatureCacheKey);
        if (!signaturePromise) {
          signaturePromise = (async () => {
            const signatureBytes = await signMessageFn(
              new TextEncoder().encode(`join:${ensuredMatchId}`),
            );
            const signature = bs58.encode(signatureBytes);
            joinSignatures.set(signatureCacheKey, signature);
            if (typeof window !== 'undefined') {
              sessionStorage.setItem(signatureCacheKey, signature);
            }
            return signature;
          })();
          joinSignaturePromises.set(signatureCacheKey, signaturePromise);
        }
        try {
          authSignature = await signaturePromise;
        } catch (signError) {
          const message =
            signError instanceof Error
              ? signError.message
              : 'Unable to sign join message. Please try again.';
          setStatus({ reconnecting: false, error: new Error(message) });
          if (joinSignaturePromises.get(signatureCacheKey) === signaturePromise) {
            joinSignaturePromises.delete(signatureCacheKey);
          }
          releaseConnectionLock();
          return;
        } finally {
          if (joinSignaturePromises.get(signatureCacheKey) === signaturePromise) {
            joinSignaturePromises.delete(signatureCacheKey);
          }
        }
      } else {
        joinSignatures.set(signatureCacheKey, authSignature);
      }

      try {
        setStatus({ reconnecting: true, error: undefined });
        if (!activeClient) {
          activeClient = new Client(endpoint);
        }
        if (
          activeRoom &&
          (activeMatchId !== ensuredMatchId || activeWallet !== ensuredWalletAddress)
        ) {
          const previousKey =
            activeMatchId && activeWallet
              ? connectionKeyFor(activeMatchId, activeWallet)
              : undefined;
          if (previousKey) {
            standbyRooms.set(previousKey, activeRoom);
          }
          activeRoom = undefined;
          activeMatchId = undefined;
          activeWallet = undefined;
        }
        let joinCluster = match.cluster ?? cluster;
        const clientSeedKey = `${ensuredMatchId}:${ensuredWalletAddress}`;
        let clientSeed = clientSeeds.get(clientSeedKey);
        if (!clientSeed) {
          clientSeed = crypto.randomUUID().replace(/-/g, '');
          clientSeeds.set(clientSeedKey, clientSeed);
        }

        const attemptJoinRoom = async (
          currentMatch: Awaited<ReturnType<typeof fetchMatchDetails>>,
          attempt = 0,
        ): Promise<{ room: Room; cluster: string }> => {
          if (!activeClient) {
            throw new Error('Colyseus client not initialized');
          }
          if (!currentMatch.roomId) {
            throw new Error('Match room is not ready');
          }
          const resolvedCluster = currentMatch.cluster ?? cluster;
          try {
            const joinedRoom = await activeClient.joinById(currentMatch.roomId, {
              cluster: resolvedCluster,
              wallet: ensuredWalletAddress,
              signature: authSignature,
              matchId: ensuredMatchId,
              clientSeed,
            });
            return { room: joinedRoom, cluster: resolvedCluster };
          } catch (joinError) {
            console.error(
              'Failed to join match room',
              joinError instanceof Error ? joinError : { joinError },
            );
            if (attempt >= MAX_ROOM_JOIN_ATTEMPTS - 1) {
              throw joinError instanceof Error ? joinError : new Error('Unable to join match room');
            }
            setStatus({ reconnecting: true, error: undefined });
            await sleep(600 * (attempt + 1));
            const refreshed = await fetchMatchDetails(ensuredMatchId).catch(() => null);
            const retryMatch = refreshed?.roomId ? refreshed : currentMatch;
            return attemptJoinRoom(retryMatch, attempt + 1);
          }
        };

        const { room, cluster: resolvedCluster } = await attemptJoinRoom(match);
        joinCluster = resolvedCluster;
        activeRoom = room;
        activeMatchId = ensuredMatchId;
        activeWallet = ensuredWalletAddress;
        standbyRooms.delete(connectionKey);
        clientSeeds.set(clientSeedKey, clientSeed);

        let lastSeedCommitSentAt = 0;
        const sendSeedCommitment = () => {
          if (!authSignature) {
            return;
          }
          const now = Date.now();
          if (now - lastSeedCommitSentAt < 1000) {
            return;
          }
          lastSeedCommitSentAt = now;
          try {
            room.send('join', {
              matchId: ensuredMatchId,
              clientSeed,
              signature: authSignature,
              cluster: joinCluster,
            });
          } catch (sendError) {
            console.warn('Failed to send join commitment', sendError);
          }
        };

        sendSeedCommitment();

        const connectionWallet = ensuredWalletAddress;
        const connectionMatchId = ensuredMatchId;

        const handleStateChange = (rawState: unknown) => {
          if (connectionWallet !== activeWallet || connectionMatchId !== activeMatchId) {
            return;
          }
          const state = rawState as {
            matchId: string;
            wagerJson: string;
            targetScore: number;
            currentTurnWallet?: string;
            timeline?: ForEachable<RawTimelineEntry>;
            players?: ForEachable<RawRoomPlayer>;
            activeAnomalyJson?: string;
            nextAnomalyJson?: string;
            anomalyHistoryJson?: string;
            playerPowerJson?: string;
            pendingDebuffsJson?: string;
            currentHandJson?: string;
            currentHandOwner?: string;
            currentHandSequence?: number;
            deckStatesJson?: string;
            turnDeadlineIso?: string;
            turnSecondsRemaining?: number;
            tempoStateJson?: string;
            chatMessages?: ForEachable<RawChatMessage>;
            escrowedWalletsJson?: string;
            seedCommitmentsJson?: string;
            status?: string;
            playerRunStatesJson?: string;
            playerShopStatesJson?: string;
            phase?: string;
          };
          const players: ClientMatchState['players'] = [];
          if (state.players && typeof state.players.forEach === 'function') {
            state.players.forEach((player) => {
              if (!player || typeof player.wallet !== 'string') {
                return;
              }
              players.push({
                wallet: player.wallet,
                displayName: typeof player.displayName === 'string' ? player.displayName : '',
                avatarSeed: typeof player.avatarSeed === 'string' ? player.avatarSeed : '',
              });
            });
          }
          const parsedWagerRaw = safeParseJson<{
            currency?: Wager['currency'];
            amountLamports?: unknown;
          }>(state.wagerJson, { currency: 'SOL', amountLamports: '0' });
          const parsedWager: ClientMatchState['wager'] = {
            currency: parsedWagerRaw.currency === 'USDC' ? 'USDC' : 'SOL',
            amountLamports: (() => {
              const raw = parsedWagerRaw.amountLamports;
              if (typeof raw === 'string') {
                return raw;
              }
              if (typeof raw === 'bigint') {
                return raw.toString();
              }
              if (typeof raw === 'number') {
                return Math.round(raw).toString();
              }
              return '0';
            })(),
          };
          const timeline: TurnResolution[] = [];
          if (state.timeline && typeof state.timeline.forEach === 'function') {
            state.timeline.forEach((turn) => {
              if (!turn) {
                return;
              }
              const breakdown = safeParseJson<ScoreBreakdown | null>(turn.breakdownJson, null);
              const player = typeof turn.player === 'string' ? turn.player : '';
              if (!player || !breakdown) {
                return;
              }
              const sequence =
                typeof turn.sequence === 'number' ? turn.sequence : timeline.length + 1;
              timeline.push({
                sequence,
                player,
                cardsPlayed: safeParseJson<DeckCard[]>(turn.cardsJson, []),
                modifiers: safeParseJson<ModifierCard[]>(turn.modifiersJson, []),
                breakdown,
              });
            });
          }

          const chatMessages: ClientMatchState['chatMessages'] = [];
          if (state.chatMessages && typeof state.chatMessages.forEach === 'function') {
            state.chatMessages.forEach((chat) => {
              if (!chat) {
                return;
              }
              const wallet = typeof chat.wallet === 'string' ? chat.wallet : '';
              const message = typeof chat.message === 'string' ? chat.message : '';
              if (!wallet || !message) {
                return;
              }
              const displayName =
                typeof chat.displayName === 'string' && chat.displayName
                  ? chat.displayName
                  : shortKey(wallet);
              const timestamp =
                typeof chat.timestamp === 'number' && Number.isFinite(chat.timestamp)
                  ? chat.timestamp
                  : Date.now();
              chatMessages.push({ wallet, displayName, message, timestamp });
            });
          }
          chatMessages.sort((a, b) => a.timestamp - b.timestamp);
          let activeAnomaly: RunAnomaly | null = null;
          if (state.activeAnomalyJson) {
            try {
              activeAnomaly = JSON.parse(state.activeAnomalyJson) as RunAnomaly;
            } catch (error) {
              console.warn('Failed to parse active anomaly', error);
            }
          }

          let nextAnomaly: RunAnomaly | null = null;
          if (state.nextAnomalyJson) {
            try {
              nextAnomaly = JSON.parse(state.nextAnomalyJson) as RunAnomaly;
            } catch (error) {
              console.warn('Failed to parse next anomaly', error);
            }
          }

          let anomalyHistory: RunAnomaly[] | undefined;
          if (state.anomalyHistoryJson) {
            try {
              anomalyHistory = JSON.parse(state.anomalyHistoryJson) as RunAnomaly[];
            } catch (error) {
              console.warn('Failed to parse anomaly history', error);
            }
          }

          let playerPowers: Record<string, string[]> | undefined;
          if (state.playerPowerJson) {
            try {
              playerPowers = JSON.parse(state.playerPowerJson) as Record<string, string[]>;
            } catch (error) {
              console.warn('Failed to parse player powers', error);
            }
          }

          let pendingDebuffs:
            | Record<string, { scorePenalty?: number; suitLock?: string; shieldCharges?: number }>
            | undefined;
          if (state.pendingDebuffsJson) {
            try {
              pendingDebuffs = JSON.parse(state.pendingDebuffsJson) as Record<
                string,
                { scorePenalty?: number; suitLock?: string; shieldCharges?: number }
              >;
            } catch (error) {
              console.warn('Failed to parse pending debuffs', error);
            }
          }

          let deckStates: Record<string, { drawPile: string[]; discardPile: string[] }> | undefined;
          if (state.deckStatesJson) {
            try {
              deckStates = JSON.parse(state.deckStatesJson) as Record<
                string,
                { drawPile: string[]; discardPile: string[] }
              >;
            } catch (error) {
              console.warn('Failed to parse deck states', error);
            }
          }

          let parsedHand: DeckCard[] | undefined;
          if (state.currentHandJson) {
            try {
              parsedHand = JSON.parse(state.currentHandJson) as DeckCard[];
            } catch (error) {
              console.warn('Failed to parse current hand', error);
            }
          }
          const currentHandOwner = state.currentHandOwner || undefined;
          const currentHandSequence = state.currentHandSequence ?? undefined;
          const currentHand =
            connectionWallet && currentHandOwner === connectionWallet
              ? (parsedHand ?? [])
              : undefined;

          const turnDeadlineIso = state.turnDeadlineIso || undefined;
          const turnSecondsRemaining =
            typeof state.turnSecondsRemaining === 'number' && state.turnSecondsRemaining > 0
              ? state.turnSecondsRemaining
              : undefined;

          let tempoState: Record<string, number> | undefined;
          if (state.tempoStateJson) {
            try {
              tempoState = JSON.parse(state.tempoStateJson) as Record<string, number>;
            } catch (error) {
              console.warn('Failed to parse tempo state', error);
            }
          }

          const parsedPlayerRunStates = state.playerRunStatesJson
            ? safeParseJson<Record<string, ClientPlayerRunState>>(state.playerRunStatesJson, {})
            : undefined;
          const playerRunStates =
            parsedPlayerRunStates && Object.keys(parsedPlayerRunStates).length > 0
              ? parsedPlayerRunStates
              : undefined;
          const playerShopStates = state.playerShopStatesJson
            ? safeParseJson<Record<string, RunShopState>>(state.playerShopStatesJson, {})
            : undefined;
          const phase = state.phase === 'shop' ? 'shop' : 'play';
          const escrowedWallets = state.escrowedWalletsJson
            ? safeParseJson<string[]>(state.escrowedWalletsJson, [])
            : [];
          const seedCommitments = state.seedCommitmentsJson
            ? safeParseJson<string[]>(state.seedCommitmentsJson, [])
            : [];
          if (connectionWallet && !seedCommitments.includes(connectionWallet)) {
            sendSeedCommitment();
          } else if (connectionWallet) {
            lastSeedCommitSentAt = 0;
          }
          const matchStatus = typeof state.status === 'string' ? state.status : undefined;
          const previousSnapshot = useRoomStoreBase.getState().state;
          const persistedSettlement =
            previousSnapshot?.matchId === state.matchId ? previousSnapshot?.settlement : undefined;
          const persistedPendingSettlement =
            previousSnapshot?.matchId === state.matchId
              ? previousSnapshot?.pendingSettlement
              : undefined;

          setState({
            matchId: state.matchId,
            wager: parsedWager,
            players,
            targetScore: state.targetScore,
            currentTurnWallet: state.currentTurnWallet,
            timeline,
            chatMessages,
            activeAnomaly,
            nextAnomaly,
            anomalyHistory,
            playerPowers,
            pendingDebuffs,
            currentHand,
            currentHandOwner,
            currentHandSequence,
            deckStates,
            turnDeadlineIso,
            turnSecondsRemaining,
            tempoState,
            playerRunStates,
            playerShopStates,
            phase,
            escrowedWallets,
            seedCommitments,
            status: matchStatus,
            settlement: persistedSettlement,
            pendingSettlement: persistedPendingSettlement,
          });
        };

        room.onStateChange(handleStateChange);
        stateHydrators.set(connectionKey, handleStateChange);
        handleStateChange(room.state);

        room.onMessage('event', (rawEvent: unknown) => {
          if (connectionWallet !== activeWallet || connectionMatchId !== activeMatchId) {
            return;
          }
          if (!rawEvent || typeof rawEvent !== 'object') {
            return;
          }
          const event = rawEvent as { type?: string; [key: string]: unknown };
          switch (event.type) {
            case 'seed:request': {
              lastSeedCommitSentAt = 0;
              sendSeedCommitment();
              break;
            }
            case 'chat:message': {
              const message = event.message as {
                wallet?: string;
                displayName?: string;
                message?: string;
                timestamp?: number;
              };
              if (
                !message ||
                typeof message.wallet !== 'string' ||
                typeof message.message !== 'string'
              ) {
                break;
              }
              const snapshot = useRoomStoreBase.getState().state;
              if (!snapshot) {
                break;
              }
              const normalized = {
                wallet: message.wallet,
                displayName:
                  typeof message.displayName === 'string' && message.displayName
                    ? message.displayName
                    : shortKey(message.wallet),
                message: message.message,
                timestamp:
                  typeof message.timestamp === 'number' && Number.isFinite(message.timestamp)
                    ? message.timestamp
                    : Date.now(),
              };
              const merged = [...(snapshot.chatMessages ?? []), normalized];
              const limited =
                merged.length > CHAT_MESSAGE_LIMIT
                  ? merged.slice(merged.length - CHAT_MESSAGE_LIMIT)
                  : merged;
              useRoomStoreBase.getState().setState({
                ...snapshot,
                chatMessages: limited,
              });
              break;
            }
            case 'hand:dealt': {
              const cards = Array.isArray(event.cards)
                ? (event.cards as DeckCard[]).map((card) => ({ ...card }))
                : [];
              const sequence =
                typeof event.sequence === 'number' && Number.isFinite(event.sequence)
                  ? event.sequence
                  : undefined;
              if (!cards.length) {
                break;
              }
              const snapshot = useRoomStoreBase.getState().state;
              if (!snapshot) {
                break;
              }
              useRoomStoreBase.getState().setState({
                ...snapshot,
                currentHandOwner: connectionWallet ?? snapshot.currentHandOwner,
                currentHandSequence: sequence ?? snapshot.currentHandSequence,
                currentHand: cards,
              });
              break;
            }
            case 'hand:cleared': {
              const snapshot = useRoomStoreBase.getState().state;
              if (!snapshot || snapshot.currentHandOwner !== connectionWallet) {
                break;
              }
              useRoomStoreBase.getState().setState({
                ...snapshot,
                currentHand: [],
                currentHandOwner: undefined,
              });
              break;
            }
            case 'power:used': {
              const by = typeof event.by === 'string' ? event.by : '';
              const cards = Array.isArray(event.cards)
                ? (event.cards as Array<{ id?: string }>).map((card) =>
                    card?.id ? (sabotageNameIndex.get(card.id) ?? card.id) : 'unknown power',
                  )
                : [];
              if (cards.length) {
                // Visual feedback handled by game UI
              }
              break;
            }
            case 'shield:consumed': {
              const wallet = typeof event.wallet === 'string' ? event.wallet : '';
              const source = typeof event.source === 'string' ? event.source : 'a sabotage';
              const effect = typeof event.effect === 'string' ? event.effect : '';
              const blocked =
                effect === 'opponentSuitLock'
                  ? 'a suit lock'
                  : effect === 'opponentScorePenalty'
                    ? 'a poison'
                    : 'an attack';
              // Visual feedback handled by game UI
              break;
            }
            case 'speed:bonus': {
              const wallet = typeof event.by === 'string' ? event.by : '';
              const elapsed = typeof event.elapsedMs === 'number' ? event.elapsedMs : undefined;
              const seconds =
                elapsed !== undefined ? Math.max(0, Math.round(elapsed / 100) / 10) : undefined;
              const streak = typeof event.streak === 'number' ? event.streak : undefined;
              const bonus = typeof event.bonus === 'number' ? event.bonus : undefined;
              // Visual feedback handled by game UI
              break;
            }
            case 'tempo:charged': {
              const wallet = typeof event.wallet === 'string' ? event.wallet : '';
              const streak = typeof event.streak === 'number' ? event.streak : TEMPO_MAX_STREAK;
              // Visual feedback handled by game UI
              break;
            }
            case 'tempo:reset': {
              const wallet = typeof event.wallet === 'string' ? event.wallet : '';
              // Visual feedback handled by game UI
              break;
            }
            case 'tempo:reward': {
              const wallet = typeof event.wallet === 'string' ? event.wallet : '';
              const rewardType = typeof event.rewardType === 'string' ? event.rewardType : 'card';
              // Visual feedback handled by game UI
              break;
            }
            case 'turn:timeout': {
              const wallet = typeof event.wallet === 'string' ? event.wallet : '';
              const penalty =
                typeof event.penalty === 'number' ? event.penalty : TIMEOUT_PENALTY_POINTS;
              // Visual feedback handled by game UI
              break;
            }
            case 'shop:opened':
            case 'shop:closed': {
              // Visual feedback handled by game UI
              break;
            }
            case 'shop:ready': {
              const wallet = typeof event.wallet === 'string' ? event.wallet : '';
              const readyCount = typeof event.readyCount === 'number' ? event.readyCount : 1;
              // Visual feedback handled by game UI
              break;
            }
            case 'shop:buy': {
              const wallet = typeof event.wallet === 'string' ? event.wallet : '';
              const applied = event.applied as { kind?: string; id?: string } | undefined;
              // Visual feedback handled by game UI
              break;
            }
            case 'shop:reroll': {
              const wallet = typeof event.wallet === 'string' ? event.wallet : '';
              const cost = typeof event.cost === 'number' ? event.cost : 0;
              // Visual feedback handled by game UI
              break;
            }
            case 'shop:consumable': {
              const wallet = typeof event.wallet === 'string' ? event.wallet : '';
              const consumableId =
                typeof event.consumableId === 'string' ? event.consumableId : 'a consumable';
              // Visual feedback handled by game UI
              break;
            }
            case 'run:resource': {
              const wallet = typeof event.wallet === 'string' ? event.wallet : '';
              const chipsDelta = typeof event.chipsDelta === 'number' ? event.chipsDelta : 0;
              const heartsDelta = typeof event.heartsDelta === 'number' ? event.heartsDelta : 0;
              const multiplier =
                typeof event.multiplierApplied === 'number' ? event.multiplierApplied : 0;
              if (wallet) {
                const parts: string[] = [];
                if (chipsDelta) {
                  parts.push(`${chipsDelta > 0 ? '+' : ''}${chipsDelta} chips`);
                }
                if (heartsDelta) {
                  parts.push(`${heartsDelta > 0 ? '+' : ''}${heartsDelta} hearts`);
                }
                if (multiplier) {
                  parts.push(`x${(1 + multiplier).toFixed(2)} multiplier`);
                }
                // Visual feedback handled by game UI
              }
              break;
            }
            case 'run:heartsDepleted': {
              toast.error('Shared hearts depleted! Every hand counts.');
              break;
            }
            case 'anomaly:activated': {
              const anomaly = event.anomaly as { description?: string };
              if (anomaly?.description) {
                toast.warning(`Anomaly activated: ${anomaly.description}`);
              }
              break;
            }
            case 'anomaly:expired': {
              const anomaly = event.anomaly as { description?: string };
              if (anomaly?.description) {
                toast(`${anomaly.description} anomaly has faded.`);
              }
              break;
            }
            case 'forfeit': {
              const wallet = typeof event.wallet === 'string' ? event.wallet : '';
              if (wallet) {
                toast.error(`${shortKey(wallet)} forfeited the match.`);
              }
              // Clear opponent disconnect state when forfeit happens
              const forfeitSnapshot = useRoomStoreBase.getState().state;
              if (forfeitSnapshot) {
                useRoomStoreBase.getState().setState({
                  ...forfeitSnapshot,
                  opponentDisconnect: null,
                });
              }
              break;
            }
            case 'player:disconnected': {
              const wallet = typeof event.wallet === 'string' ? event.wallet : '';
              const remainingTime =
                typeof event.remainingTime === 'number' ? event.remainingTime : 90000;
              // Only show if it's the opponent who disconnected
              if (wallet && wallet !== activeWallet) {
                const snapshot = useRoomStoreBase.getState().state;
                if (snapshot) {
                  useRoomStoreBase.getState().setState({
                    ...snapshot,
                    opponentDisconnect: { wallet, remainingTime },
                  });
                }
                toast.warning('Opponent disconnected');
              }
              break;
            }
            case 'player:reconnected': {
              const wallet = typeof event.wallet === 'string' ? event.wallet : '';
              // Clear disconnect banner if opponent reconnected
              if (wallet && wallet !== activeWallet) {
                const snapshot = useRoomStoreBase.getState().state;
                if (snapshot) {
                  useRoomStoreBase.getState().setState({
                    ...snapshot,
                    opponentDisconnect: null,
                  });
                }
                toast.info('Opponent reconnected');
              }
              break;
            }
            case 'disconnect:tick': {
              const wallet = typeof event.wallet === 'string' ? event.wallet : '';
              const remainingTime =
                typeof event.remainingTime === 'number' ? event.remainingTime : 0;
              // Update remaining time for opponent disconnect
              if (wallet && wallet !== activeWallet) {
                const snapshot = useRoomStoreBase.getState().state;
                if (snapshot && snapshot.opponentDisconnect) {
                  useRoomStoreBase.getState().setState({
                    ...snapshot,
                    opponentDisconnect: { wallet, remainingTime },
                  });
                }
              }
              break;
            }
            case 'settlement:pending': {
              const winner = typeof event.winner === 'string' ? event.winner : '';
              if (!winner) {
                break;
              }
              const snapshot = useRoomStoreBase.getState().state;
              if (!snapshot) {
                break;
              }
              const rawReason = typeof event.reason === 'string' ? event.reason : 'standard';
              const reason: SettlementReason =
                rawReason === 'forfeit' || rawReason === 'turnLimit' ? rawReason : 'standard';
              useRoomStoreBase.getState().setState({
                ...snapshot,
                status: 'settling',
                pendingSettlement: {
                  winner,
                  reason,
                },
              });
              break;
            }
            case 'settlement:failed': {
              const message =
                typeof event.message === 'string'
                  ? event.message
                  : 'Settlement failed. Please refresh.';
              toast.error(message);
              const snapshot = useRoomStoreBase.getState().state;
              if (!snapshot) {
                break;
              }
              useRoomStoreBase.getState().setState({
                ...snapshot,
                pendingSettlement: undefined,
              });
              break;
            }
            case 'settled': {
              const settlement = parseSettlementEvent(event.settlement);
              if (settlement) {
                const snapshot = useRoomStoreBase.getState().state;
                const baseState: ClientMatchState = snapshot
                  ? { ...snapshot }
                  : {
                      matchId: settlement.matchId,
                      wager: {
                        currency: settlement.currency,
                        amountLamports: '0',
                      },
                      players: [],
                      targetScore: 0,
                      timeline: [],
                      chatMessages: [],
                    };
                const winnerLabel =
                  baseState.players.find((player) => player.wallet === settlement.winner)
                    ?.displayName ?? shortKey(settlement.winner);
                useRoomStoreBase.getState().setState({
                  ...baseState,
                  settlement,
                  status: 'completed',
                  pendingSettlement: undefined,
                });
                toast.success(`${winnerLabel} won the match.`);

                // Refetch player stats to ensure UI shows latest W/L and XP
                if (connectionWallet) {
                  usePlayerStatsStore.getState().fetchStats(connectionWallet);
                }

                // Invalidate closeable matches to show notification badge immediately
                void queryClient.invalidateQueries({ queryKey: ['matches', 'closeable'] });
              } else {
                toast.success('Match settled — check results for the final payout.');
                // Invalidate closeable matches even without settlement details
                void queryClient.invalidateQueries({ queryKey: ['matches', 'closeable'] });
              }
              break;
            }
            case 'progression:update': {
              const progressionEvent = event as {
                type: 'progression:update';
                winner?: ProgressionUpdate;
                loser?: ProgressionUpdate;
              };
              const currentWallet = connectionWallet;

              // Handle progression for the current player
              if (currentWallet && progressionEvent.winner?.wallet === currentWallet) {
                usePlayerStatsStore.getState().handleProgressionUpdate(progressionEvent.winner);
                if (progressionEvent.winner.xpGained > 0) {
                  toast.success(`+${progressionEvent.winner.xpGained} XP earned!`);
                }
              } else if (currentWallet && progressionEvent.loser?.wallet === currentWallet) {
                usePlayerStatsStore.getState().handleProgressionUpdate(progressionEvent.loser);
                if (progressionEvent.loser.xpGained > 0) {
                  toast.info(`+${progressionEvent.loser.xpGained} XP earned`);
                }
              }
              break;
            }
            case 'joker:evolved': {
              const wallet = typeof event.wallet === 'string' ? event.wallet : '';
              const fromJoker = event.fromJoker as
                | { id?: string; name?: string; tier?: string }
                | undefined;
              const toJoker = event.toJoker as
                | { id?: string; name?: string; tier?: string }
                | undefined;
              if (wallet && fromJoker?.id && fromJoker?.name && toJoker?.id && toJoker?.name) {
                const tierEmoji = toJoker.tier === 'ascended' ? '✨' : '⚡';
                const tierLabel = toJoker.tier === 'ascended' ? 'ASCENDED' : 'AWAKENED';
                toast.success(
                  `${tierEmoji} ${fromJoker.name} evolved into ${toJoker.name}! [${tierLabel}]`,
                  { duration: 5000 },
                );
                // Store evolution in state for post-game summary
                const snapshot = useRoomStoreBase.getState().state;
                if (snapshot) {
                  const evolution = {
                    wallet,
                    fromJoker: {
                      id: fromJoker.id,
                      name: fromJoker.name,
                      tier: fromJoker.tier || 'base',
                    },
                    toJoker: {
                      id: toJoker.id,
                      name: toJoker.name,
                      tier: toJoker.tier || 'awakened',
                    },
                  };
                  useRoomStoreBase.getState().setState({
                    ...snapshot,
                    evolutions: [...(snapshot.evolutions || []), evolution],
                  });
                }
              }
              break;
            }
            case 'rematch:requested': {
              const wallet = typeof event.wallet === 'string' ? event.wallet : '';
              const opponentRequested = event.opponentRequested === true;
              if (wallet) {
                const snapshot = useRoomStoreBase.getState().state;
                if (snapshot) {
                  const currentRequests = snapshot.rematchRequests || [];
                  if (!currentRequests.includes(wallet)) {
                    useRoomStoreBase.getState().setState({
                      ...snapshot,
                      rematchRequests: [...currentRequests, wallet],
                    });
                  }
                }
                // Show toast based on who requested
                if (wallet === connectionWallet) {
                  if (opponentRequested) {
                    toast.success('Rematch confirmed! Creating new match...');
                  } else {
                    toast.info('Rematch requested! Waiting for opponent...');
                  }
                } else {
                  toast.info(`${shortKey(wallet)} wants a rematch!`);
                }
              }
              break;
            }
            case 'rematch:confirmed': {
              const players = Array.isArray(event.players) ? event.players : [];
              const wager = event.wager as
                | { currency?: string; amountLamports?: string }
                | undefined;
              if (players.length === 2 && wager) {
                const snapshot = useRoomStoreBase.getState().state;
                if (snapshot) {
                  useRoomStoreBase.getState().setState({
                    ...snapshot,
                    rematchConfirmed: true,
                    rematchWager: {
                      currency: (wager.currency as Wager['currency']) || 'SOL',
                      amountLamports: wager.amountLamports || '0',
                    },
                  });
                }
                toast.success('Rematch confirmed! Both players accepted.');
              }
              break;
            }
            case 'sacrifice:activated': {
              const wallet = typeof event.wallet === 'string' ? event.wallet : '';
              const sacrificeType =
                typeof event.sacrificeType === 'string' ? event.sacrificeType : '';
              const description =
                typeof event.description === 'string' ? event.description : 'Sacrifice activated';
              if (wallet) {
                const isMe = wallet === connectionWallet;
                const playerLabel = isMe ? 'You' : shortKey(wallet);
                const sacrificeLabel =
                  sacrificeType === 'scoreBoost'
                    ? '💥 Score Boost'
                    : sacrificeType === 'drawCards'
                      ? '🃏 Draw Cards'
                      : '✨ Cleanse';
                if (isMe) {
                  toast.success(`${sacrificeLabel} activated! ${description}`);
                } else {
                  toast.warning(`${playerLabel} sacrificed a heart for ${sacrificeLabel}!`);
                }
              }
              break;
            }
            case 'doubleOrNothing:proposed': {
              const proposedBy = typeof event.proposedBy === 'string' ? event.proposedBy : '';
              const proposedWager = event.proposedWager as
                | { currency?: string; amountLamports?: string }
                | undefined;
              if (proposedBy && proposedWager) {
                const snapshot = useRoomStoreBase.getState().state;
                if (snapshot) {
                  useRoomStoreBase.getState().setState({
                    ...snapshot,
                    doubleOrNothingProposed: true,
                    doubleOrNothingProposedBy: proposedBy,
                    doubleOrNothingWager: {
                      currency: (proposedWager.currency as Wager['currency']) || 'SOL',
                      amountLamports: proposedWager.amountLamports || '0',
                    },
                  });
                }
                // Show toast based on who proposed
                if (proposedBy === connectionWallet) {
                  toast.success('Double-or-Nothing proposed! Waiting for opponent...');
                } else {
                  toast.info('Opponent proposed Double-or-Nothing! 2x the stakes!');
                }
              }
              break;
            }
            case 'doubleOrNothing:confirmed': {
              const players = Array.isArray(event.players) ? event.players : [];
              const wager = event.wager as
                | { currency?: string; amountLamports?: string }
                | undefined;
              if (players.length === 2 && wager) {
                const snapshot = useRoomStoreBase.getState().state;
                if (snapshot) {
                  useRoomStoreBase.getState().setState({
                    ...snapshot,
                    doubleOrNothingConfirmed: true,
                    doubleOrNothingWager: {
                      currency: (wager.currency as Wager['currency']) || 'SOL',
                      amountLamports: wager.amountLamports || '0',
                    },
                  });
                }
                toast.success('Double-or-Nothing confirmed! Creating new match with 2x stakes...');
              }
              break;
            }
            default:
              break;
          }
        });

        room.onLeave((code) => {
          const key = connectionKeyFor(connectionMatchId, connectionWallet);
          if (standbyRooms.get(key) === room) {
            standbyRooms.delete(key);
          }
          stateHydrators.delete(key);
          if (
            activeRoom === room &&
            connectionMatchId === activeMatchId &&
            connectionWallet === activeWallet
          ) {
            activeRoom = undefined;
            activeMatchId = undefined;
            activeWallet = undefined;
            if (code !== 1000) {
              toast.warning('Disconnected from room, attempting to reconnect…');
              connect();
            }
          }
        });

        room.onError((code, message) => {
          if (connectionWallet !== activeWallet || connectionMatchId !== activeMatchId) {
            return;
          }
          toast.error(`Room error ${code}: ${message}`);
          setStatus({ error: new Error(message) });
        });

        setStatus({ reconnecting: false, error: undefined });
        releaseConnectionLock();
      } catch (err) {
        setStatus({ reconnecting: false, error: err as Error });
        releaseConnectionLock();
      }
    }
  }, [cluster, matchId, setState, setStatus, walletAddress, signMessage]);

  const sendShopAction = useCallback(
    async (payload: unknown) => {
      if (!matchId) {
        throw new Error('No match id available');
      }
      if (!activeRoom) {
        throw new Error('Room not connected');
      }
      activeRoom.send('shop', payload);
    },
    [matchId],
  );

  const purchaseOffer = useCallback(
    async (offerId: string) => {
      await sendShopAction({ type: 'buy', offerId });
    },
    [sendShopAction],
  );

  const rerollShop = useCallback(async () => {
    await sendShopAction({ type: 'reroll' });
  }, [sendShopAction]);

  const useShopConsumable = useCallback(
    async (consumableId: string) => {
      await sendShopAction({ type: 'useConsumable', consumableId });
    },
    [sendShopAction],
  );

  const signalShopReady = useCallback(async () => {
    await sendShopAction({ type: 'ready' });
  }, [sendShopAction]);

  const forfeit = useCallback(async () => {
    if (!matchId) {
      throw new Error('No match id available');
    }
    if (!activeRoom) {
      throw new Error('Room not connected');
    }
    activeRoom.send('forfeit');
  }, [matchId]);

  const sendRematchRequest = useCallback(async () => {
    if (!matchId) {
      throw new Error('No match id available');
    }
    if (!activeRoom) {
      throw new Error('Room not connected');
    }
    activeRoom.send('rematch:request');
  }, [matchId]);

  const sendSacrifice = useCallback(
    async (type: 'scoreBoost' | 'drawCards' | 'cleanse') => {
      if (!matchId) {
        throw new Error('No match id available');
      }
      if (!activeRoom) {
        throw new Error('Room not connected');
      }
      activeRoom.send('sacrifice', { type });
    },
    [matchId],
  );

  const sendDoubleOrNothingRequest = useCallback(async () => {
    if (!matchId) {
      throw new Error('No match id available');
    }
    if (!activeRoom) {
      throw new Error('Room not connected');
    }
    activeRoom.send('doubleOrNothing:request');
  }, [matchId]);

  const disconnect = useCallback(async () => {
    const roomsToClose: Array<{ key?: string; room: Room }> = [];
    if (activeRoom) {
      const key =
        activeMatchId && activeWallet ? connectionKeyFor(activeMatchId, activeWallet) : undefined;
      roomsToClose.push({ key, room: activeRoom });
      if (key) {
        stateHydrators.delete(key);
      }
    }
    standbyRooms.forEach((room, key) => {
      roomsToClose.push({ key, room });
      stateHydrators.delete(key);
    });
    standbyRooms.clear();
    activeRoom = undefined;
    activeMatchId = undefined;
    activeWallet = undefined;

    await Promise.all(
      roomsToClose.map(async ({ room }) => {
        try {
          await room.leave(true);
        } catch {
          // Ignore errors during disconnect cleanup.
        }
      }),
    );
  }, []);

  const state = useRoomStoreBase((s) => s.state);
  const playTurn = useCallback(
    async ({
      playedCardIds,
      powerCardIds = [],
      declaredHand = 'sabotage',
    }: {
      playedCardIds: string[];
      powerCardIds?: string[];
      declaredHand?: string;
    }) => {
      if (!matchId) {
        throw new Error('No match id available');
      }
      if (!walletAddress) {
        throw new Error('Wallet not connected');
      }
      if (!activeRoom) {
        throw new Error('Room not connected');
      }
      if (playedCardIds.length === 0 && powerCardIds.length === 0) {
        throw new Error('Select cards or a power card before playing.');
      }
      const seedKey = `${matchId}:${walletAddress}`;
      const clientSeed = clientSeeds.get(seedKey) ?? crypto.randomUUID().replace(/-/g, '');
      clientSeeds.set(seedKey, clientSeed);

      const currentState = useRoomStoreBase.getState().state;
      const sequence = currentState?.currentHandSequence ?? currentState?.timeline?.length ?? 0;

      activeRoom.send('turn', {
        matchId,
        sequence,
        payload: {
          playedCardIds,
          modifierCardIds: powerCardIds,
          declaredHand,
        },
        signature: ''.padEnd(64, '0'),
        clientSeed,
      });
    },
    [matchId, walletAddress],
  );

  const sendChat = useCallback(
    async (message: string) => {
      const trimmed = message.trim();
      if (!trimmed) {
        return;
      }
      if (!matchId) {
        throw new Error('No match id available');
      }
      if (!walletAddress) {
        throw new Error('Wallet not connected');
      }
      if (!activeRoom) {
        throw new Error('Room not connected');
      }
      activeRoom.send('chat', { message: trimmed });
    },
    [matchId, walletAddress],
  );

  return {
    connect,
    disconnect,
    state,
    reconnecting,
    error,
    playTurn,
    sendChat,
    purchaseOffer,
    rerollShop,
    useShopConsumable,
    signalShopReady,
    forfeit,
    sendRematchRequest,
    sendSacrifice,
    sendDoubleOrNothingRequest,
  };
};
